import { VStack } from '@chakra-ui/react';
import { ContentLayout, Loader } from 'Molecules';
import { DrLearnMore } from './DrLearnMore';
import { AssessDisclosureRequirement } from './AssessDisclosureRequirement';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MetricsMaterialityTable } from './MetricsMaterialityTable';
import {
  MaterialityFields,
  MaterialityState,
  MaterialityStatus,
  MetricMaterialityState,
  StandardMaterialityState,
  VSMEMaterialityStatus,
} from '../DoubleMaterialityAssessment.d';
import {
  checkMateriality,
  processMfMetricsData,
  updateAllMateriality,
  useSaveDrMetrics,
} from './DrMateriality.hooks';
import { MaterialMetric, Metric } from '../../MaterialityAssessment.d';
import { ParentInfoBox } from '../MaterialityUtils';
import {
  COLLECT_ONLY_OPTIONS,
  DisclosureRequirementInfo,
  METRIC_MATERIALITY_OPTIONS,
  VSME_METRIC_MATERIALITY_OPTIONS,
} from './DrMaterialityUtils';
import { DataCollectionLevel } from 'containers/Esrs/pieces/DataCollection';
import { MFDatapointsDRBrowser } from './MFDatapointsDRBrowser';
import {
  getStandardMateriality,
  useGetStandardMaterialityData,
  useMapDisclosureRequirement,
} from '../DoubleMaterialityAssessment.hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { isParentMaterial } from '../../MaterialityAssessment.hooks';
import { Typography } from 'Tokens';
import { Breadcrumbs } from 'Molecules/Breadcrumbs';
import { Button } from 'Atoms';
import { ArrowNarrowRightIcon } from 'Tokens/Icons/Direction';
import { useGetMfDisclosureRequirementsMetricsQuery } from 'models';
import { useFeatureFlags } from 'containers/Navigation';
import { useCurrentCompany } from 'utils/hooks';
import { MaterialityToolbar } from './MaterialityToolbar';
import { FrameworkRefs } from 'containers/Esrs/pieces/Assessment';

export type MMOmitAggregate = Omit<MaterialMetric, 'metric'> &
  Omit<MaterialMetric['metric'], 'childrenMetrics_aggregate'>;

export type MetricsMaterialityTableMetric = Metric & {
  materiality: MetricMaterialityState;
  metricAdditionalTypes: {
    reference: string;
    title: string;
    description: string;
    helpText: string;
  }[];
  metricRefNumber: string;
  metricOptions: (
    | {
        label: VSMEMaterialityStatus;
        value: MetricMaterialityState;
      }
    | {
        label: MaterialityStatus;
        value: MetricMaterialityState;
      }
  )[];
};

export const DrMateriality = () => {
  const { framework = '' } = useParams();
  const [materialMetrics, setMaterialMetrics] = useState<MMOmitAggregate[]>([]);
  const saveMetrics = useSaveDrMetrics();
  const { control, watch, setValue } = useForm<MaterialityFields>();
  const mapDisclosureRequirement = useMapDisclosureRequirement();
  const navigate = useNavigate();
  const { company } = useCurrentCompany();
  const [selectedMetricsToUpdate, setSelectedMetricsToUpdate] = useState<
    MetricsMaterialityTableMetric[]
  >([]);
  const [mutateMetrics, setMutateMetrics] = useState(false);

  const { companyFrameworks } = useFeatureFlags({ company });

  const hasOtherMetrics = useMemo(() => companyFrameworks.length >= 2, [companyFrameworks]);

  const { disclosureRequirementRef } = useParams();

  const {
    standard,
    materialStandard,
    parentStandardMaterialityData,
    hasParent,
    isGroupOwner: isGroup,
    isCollectOnly,
    loading,
  } = useGetStandardMaterialityData();

  const materialStandardId = materialStandard?.id;

  const baseMaterialMetrics: MaterialMetric[] = useMemo(
    () =>
      materialStandard?.materialMetrics.filter((metric) => {
        const isMetricInDR = metric.metric?.requirement?.reference === disclosureRequirementRef;
        return isMetricInDR;
      }) ?? [],
    [disclosureRequirementRef, materialStandard?.materialMetrics]
  );

  const isStandardRequiredByParent = useMemo(
    () =>
      parentStandardMaterialityData?.isMaterial &&
      isParentMaterial(parentStandardMaterialityData.materialMetrics),
    [materialStandard, parentStandardMaterialityData]
  );

  const [isCollectOnlyMandatory, isCollectData] = useMemo(
    () => [
      isCollectOnly && isStandardRequiredByParent,
      materialStandard?.isMaterial && isCollectOnly,
    ],
    [materialStandard, isCollectOnly, isStandardRequiredByParent]
  );
  const isStandardMandatory = useMemo(() => {
    const isTopicalStandardNotMandatory =
      isCollectOnly && !isStandardRequiredByParent && !standard?.isTopical;
    if (isTopicalStandardNotMandatory) return false;
    return !(standard?.isTopical ?? false);
  }, [isCollectOnly, isStandardRequiredByParent, standard]);

  const standardMateriality = useMemo(
    () =>
      getStandardMateriality(
        isCollectOnlyMandatory ?? false,
        isCollectData ?? false,
        isCollectOnly,
        isStandardMandatory,
        materialStandard
      ),
    [materialStandard, isCollectOnly, isCollectOnlyMandatory, isCollectData, isStandardMandatory]
  );

  const parentMetrics = useMemo(() => {
    return parentStandardMaterialityData?.materialMetrics ?? [];
  }, [parentStandardMaterialityData]);

  const disclosureRequirement = useMemo(
    () =>
      mapDisclosureRequirement({
        drGroups: standard?.disclosureRequirementGroups,
        drRef: disclosureRequirementRef ?? '',
        materialMetrics: baseMaterialMetrics,
        parentMaterialMetrics: parentMetrics,
        isCollectOnly: isCollectOnly ?? false,
        isGroupOwner: isGroup,
        isStandardMandatory: isStandardMandatory,
        isStandardMaterial: standardMateriality === StandardMaterialityState.material,
        isParentStandardMaterial: parentStandardMaterialityData?.isMaterial,
      }),
    [
      standard?.disclosureRequirementGroups,
      disclosureRequirementRef,
      baseMaterialMetrics,
      parentMetrics,
      isCollectOnly,
      isGroup,
      isStandardMandatory,
      standardMateriality,
      parentStandardMaterialityData?.isMaterial,
    ]
  );

  const { data, loading: mfDrLoading } = useGetMfDisclosureRequirementsMetricsQuery({
    variables: { mfDRrefs: disclosureRequirement.mfDisclosureRequirementRefs },
    skip: !disclosureRequirement.mfDisclosureRequirementRefs,
  });

  const tableData = useMemo(() => processMfMetricsData(data), [data]);

  const drMaterialMetrics: MaterialMetric[] = useMemo(
    () =>
      materialStandard?.materialMetrics.filter((metric) => {
        const isMetricInDR = metric.metric?.requirement?.reference === disclosureRequirementRef;
        const isMFMetric = metric.metric.mfDisclosureRequirements.some((met) =>
          disclosureRequirement.mfDisclosureRequirementRefs?.includes(
            met.mfDisclosureRequirement.reference
          )
        );
        return isMetricInDR || isMFMetric;
      }) ?? [],
    [
      disclosureRequirementRef,
      materialStandard?.materialMetrics,
      disclosureRequirement.mfDisclosureRequirementRefs,
    ]
  );

  useEffect(() => {
    if (!loading) {
      setMaterialMetrics(drMaterialMetrics);
    }
  }, [drMaterialMetrics, loading]);

  const isParentStandardMaterial = parentStandardMaterialityData?.isMaterial ?? false;

  useEffect(() => {
    setValue('material', disclosureRequirement?.materialityStatus ?? MaterialityState.toAssess);
  }, [disclosureRequirement.materialityStatus]);

  const material = watch('material');
  const materialValue = useMemo(() => material, [material]);

  const updateAllMetrics = (materiality: MaterialityState | null) => {
    const updatedMetrics = updateAllMateriality(
      disclosureRequirement?.metrics ?? [],
      materiality,
      drMaterialMetrics,
      disclosureRequirementRef ?? ''
    );

    setMaterialMetrics(updatedMetrics);
  };

  useEffect(() => {
    const mutation = async () => {
      try {
        await saveMetrics(
          materialMetrics,
          isGroup,
          materialStandardId,
          disclosureRequirement?.drRef
        );
      } catch (error) {
        console.error(error);
      } finally {
        setMutateMetrics(false);
      }
    };
    if (mutateMetrics) {
      mutation();
    }
  }, [materialMetrics, mutateMetrics]);
  const handleConfirm = () => {
    saveMetrics(materialMetrics, isGroup, materialStandardId, disclosureRequirement?.drRef);
    navigate(-1);
  };

  const drMateriality = useMemo(
    () => disclosureRequirement?.materialityStatus,
    [disclosureRequirement]
  );

  const materialityOptions = useMemo(() => {
    if (materialValue === null || materialValue === MaterialityState.toAssess) {
      return [];
    }
    if (isCollectOnly) {
      return COLLECT_ONLY_OPTIONS;
    }

    return (
      framework === FrameworkRefs.vsme
        ? VSME_METRIC_MATERIALITY_OPTIONS
        : METRIC_MATERIALITY_OPTIONS
    ).filter((option) => {
      if (materialValue === MaterialityState.gatherData) {
        if (
          option.value === MetricMaterialityState.material ||
          option.value === MetricMaterialityState.phasedIn
        ) {
          return false;
        }
        return true;
      } else if (materialValue === MaterialityState.phasedIn) {
        if (option.value === MetricMaterialityState.material) {
          return false;
        }
        return true;
      }
      return true;
    });
  }, [isCollectOnly, materialValue, framework]);

  const isDRAssessed = useMemo(
    () =>
      (drMateriality !== MaterialityState.toAssess && drMateriality !== null) ||
      materialMetrics.some((mm) => mm.isMaterial !== null),
    [drMateriality, materialMetrics]
  );

  const material1 = watch('material');
  const status = useMemo(() => material1 ?? MaterialityState.toAssess, [material1]);

  const isMandatoryOrMaterialMandatory = useMemo(() => {
    if (status === MaterialityState.mandatory || status === MaterialityState.materialMandatory) {
      return true;
    }
    if (isCollectOnly) {
      return isStandardMandatory;
    }
    return false;
  }, [status, isCollectOnly, isStandardMandatory]);

  const canCollectDisclosure = useMemo(
    () =>
      drMateriality === MaterialityState.collectData ||
      drMateriality === MaterialityState.doNotCollect,
    [drMateriality]
  );

  const showTable = useMemo(
    () =>
      isMandatoryOrMaterialMandatory ||
      (isCollectOnly && materialValue !== MaterialityState.doNotCollect) ||
      (!isCollectOnly && materialValue !== MaterialityState.notMaterial) ||
      !isDRAssessed,
    [materialValue, isDRAssessed, isMandatoryOrMaterialMandatory]
  );

  const parentDrMetrics = useMemo(
    () =>
      parentMetrics.filter(
        (m) => m.metric.disclosureRequirementRef === disclosureRequirement?.drRef
      ),
    [parentMetrics, disclosureRequirement]
  );

  const handleUpdateMateriality = (
    selection: MetricMaterialityState,
    metric: Metric,
    isChild?: boolean
  ) => {
    const hasPhasedInOption = !!metric?.additionalTypes?.find((additionalType) =>
      additionalType.additionalType?.reference?.includes('Phased')
    );
    setMaterialMetrics((prevMetrics) => {
      if (selection === MetricMaterialityState.phasedIn && !hasPhasedInOption) {
        return [...prevMetrics];
      }
      const foundMetric = prevMetrics.find((m) => m.metricRef === metric.reference);
      const isMaterial = checkMateriality(selection, isCollectOnly);
      const isDataGatheringOnly =
        selection === MetricMaterialityState.gatherData ||
        (selection === MetricMaterialityState.phasedIn && hasPhasedInOption);
      const isPhasedIn = selection === MetricMaterialityState.phasedIn && hasPhasedInOption;

      if (foundMetric) {
        if (isChild) {
          if (!metric.isAssessable) {
            const isAnyParentMaterial = metric.parentMetrics?.some(
              (parentMet) =>
                prevMetrics.find((m) => m.metricRef === parentMet.parentMetric.reference)
                  ?.isMaterial
            );
            if (isAnyParentMaterial && foundMetric.isMaterial && !foundMetric.isDataGatheringOnly) {
              return [...prevMetrics];
            }
            return prevMetrics.map((m) =>
              m.metricRef === metric.reference
                ? {
                    ...m,
                    isMaterial,
                    isDataGatheringOnly,
                    isPhasedIn,
                  }
                : m
            );
          }
          if (
            (foundMetric.isMaterial && !foundMetric.isDataGatheringOnly) ||
            selection === MetricMaterialityState.notMaterial
          ) {
            return [...prevMetrics];
          }
        }
        return prevMetrics.map((m) =>
          m.metricRef === metric.reference
            ? {
                ...m,
                isMaterial,
                isDataGatheringOnly,
                isPhasedIn,
              }
            : m
        );
      } else {
        return [
          ...prevMetrics,
          {
            metricRef: metric.reference,
            isMaterial,
            isDataGatheringOnly,
            isPhasedIn,
          },
        ];
      }
    });
    //handle children
    if (metric.childrenMetrics?.length > 0) {
      metric.childrenMetrics.forEach((child) => {
        if (child.childMetric)
          handleUpdateMateriality(selection, child.childMetric as Metric, true);
      });
    }
  };

  if (loading || mfDrLoading) return <Loader />;

  return (
    <ContentLayout backButton={true} header={false}>
      <VStack gap="0px" overflow="auto">
        <VStack
          spacing="16px"
          alignItems="stretch"
          width="98%"
          mt="0px"
          mb="14px"
          bg="bg.default"
          padding="24px 24px 72px 24px"
          border="1px solid"
          borderColor="border.decorative"
          borderRadius="8px"
          position="relative"
          maxWidth="1560px"
        >
          <VStack alignItems="start" gap="2px" pb="16px">
            <Typography variant="h1">{disclosureRequirement.title}</Typography>
            <Typography variant="body">Disclosure requirement assessment</Typography>
          </VStack>
          <VStack alignItems="strech" gap="8px">
            {disclosureRequirement?.parentMateriality &&
              hasParent &&
              status !== MaterialityState.mandatory &&
              !isCollectOnly && <ParentInfoBox status={disclosureRequirement?.parentMateriality} />}
            <DisclosureRequirementInfo status={status} framework={framework} />
          </VStack>
          <VStack spacing="0px" alignItems="stretch">
            <DrLearnMore
              disclosureRequirement={disclosureRequirement}
              isCollectOnly={isCollectOnly}
              isStandardMandatory={isStandardMandatory}
            />
            {!isMandatoryOrMaterialMandatory && (isCollectOnly ? canCollectDisclosure : true) && (
              <AssessDisclosureRequirement
                disableMaterial={
                  standardMateriality === StandardMaterialityState.gatherData ||
                  standardMateriality === StandardMaterialityState.phasedIn
                }
                disableMaterialButPhasedIn={
                  standardMateriality === StandardMaterialityState.gatherData
                }
                control={control}
                showTable={showTable}
                disclosureRequirement={disclosureRequirement}
                isCollectOnly={isCollectOnly}
                isStandardMandatory={isStandardMandatory}
                updateAllMateriality={updateAllMetrics}
                isParentMaterial={
                  isParentStandardMaterial &&
                  parentDrMetrics.some(
                    (m) =>
                      m.isMaterial &&
                      m.dataCollection === DataCollectionLevel.subsidiaries &&
                      m.metric.childrenMetricsCount === 0
                  )
                }
              />
            )}
            {showTable && (
              <MetricsMaterialityTable
                metrics={disclosureRequirement.metrics}
                readOnly={isMandatoryOrMaterialMandatory}
                disclosureRequirement={disclosureRequirement}
                drMateriality={materialValue}
                materialMetrics={materialMetrics}
                hasParent={hasParent}
                parentMetrics={parentMetrics}
                isCollectOnly={isCollectOnly}
                isGroup={isGroup}
                borderLeftNone={!tableData.length || !hasOtherMetrics}
                selectedMetricsToUpdate={selectedMetricsToUpdate}
                setSelectedMetricsToUpdate={setSelectedMetricsToUpdate}
                handleUpdateMateriality={handleUpdateMateriality}
              />
            )}
            {hasOtherMetrics && (
              <MFDatapointsDRBrowser
                tableData={tableData}
                materialMetrics={materialMetrics}
                setMaterialMetrics={setMaterialMetrics}
                isCollectOnly={isCollectOnly}
                isDrAssessed={isDRAssessed}
                parentMetrics={parentMetrics}
                readOnly={isMandatoryOrMaterialMandatory}
                parentMateriality={disclosureRequirement.parentMateriality}
                disclosureRequirement={disclosureRequirement}
                drMateriality={materialValue}
                hasParent={hasParent}
                isGroup={isGroup}
                drRef={disclosureRequirementRef ?? ''}
                selectedMetricsToUpdate={selectedMetricsToUpdate}
                setSelectedMetricsToUpdate={setSelectedMetricsToUpdate}
                handleUpdateMateriality={handleUpdateMateriality}
              />
            )}
            <Button
              variant="primary"
              type="submit"
              alignSelf="start"
              mt="16px"
              onClick={handleConfirm}
              rightIcon={<ArrowNarrowRightIcon color="text.onAccent" />}
            >
              Done
            </Button>
          </VStack>
        </VStack>
        {selectedMetricsToUpdate.length && (
          <MaterialityToolbar
            setSelectedMetricsToUpdate={setSelectedMetricsToUpdate}
            selectedMetricsToUpdate={selectedMetricsToUpdate}
            handleUpdateMateriality={handleUpdateMateriality}
            setMutateMetrics={setMutateMetrics}
            materialityOptions={materialityOptions}
          />
        )}
      </VStack>
    </ContentLayout>
  );
};
